import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import {
  CreditCard, IndianRupee,
  Calendar, ArrowUpRight, ArrowDownRight,
  ShoppingCart, TrendingUp
} from "lucide-react";
import { NotificationContext } from "../../context/NotificationContext";
import authAxios from "../../api/authAxios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

// 8 distinct colors — one for each possible subscription plan tier
const PLAN_COLORS = [
  '#6366f1', // indigo  — Trial
  '#10b981', // emerald — Basic Monthly
  '#3b82f6', // blue    — Basic Yearly
  '#f59e0b', // amber   — Standard Monthly
  '#f97316', // orange  — Standard Yearly
  '#8b5cf6', // violet  — Premium Monthly
  '#ec4899', // pink    — Premium Yearly
  '#14b8a6', // teal    — (overflow / future plan)
];

function MainStatCard({ title, value, subtext, icon: Icon, gradient, accent, trend, trendLabel }) {
  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      className="relative overflow-hidden rounded-3xl p-6 shadow-2xl cursor-default select-none"
      style={{ background: gradient }}
    >
      {/* Glow orb */}
      <div
        className="absolute -top-10 -right-10 w-44 h-44 rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: accent }}
      />

      {/* Top row */}
      <div className="flex items-start justify-between mb-5 relative z-10">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Icon size={22} color="#fff" strokeWidth={2.2} />
        </div>

        {trend !== undefined && (
          <div
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black"
            style={{
              background: trend >= 0 ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
              color: trend >= 0 ? '#6ee7b7' : '#fca5a5',
              border: `1px solid ${trend >= 0 ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
            }}
          >
            {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>

      {/* Label */}
      <p className="text-[10px] font-black uppercase tracking-[0.22em] mb-1.5 relative z-10 text-white/60">
        {title}
      </p>

      {/* Value */}
      <h3 className="text-4xl font-black text-white mb-2 relative z-10 leading-none drop-shadow">
        {value}
      </h3>

      {/* Subtext */}
      <p className="text-[11px] font-semibold text-white/45 relative z-10">
        {subtext || trendLabel}
      </p>

      {/* Bottom shimmer line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.35), rgba(255,255,255,0.05), transparent)' }}
      />
    </motion.div>
  );
}

export default function DashboardStats() {
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    monthly_revenue: 0,
    monthly_growth: 0,
    active_subscriptions: 0,
    expiring_soon: 0,
    failed_payments: 0,
    total_owners: 0
  });
  const [revenueData, setRevenueData] = useState([]);
  const [planDistribution, setPlanDistribution] = useState([]);

  useEffect(() => {
    const cachedStats = sessionStorage.getItem("dashboard_stats_cache");
    const cachedRev = sessionStorage.getItem("dashboard_rev_cache");
    const cachedPlan = sessionStorage.getItem("dashboard_plan_cache");

    if (cachedStats && cachedRev && cachedPlan) {
      try {
        setStats(JSON.parse(cachedStats));
        setRevenueData(JSON.parse(cachedRev));
        setPlanDistribution(JSON.parse(cachedPlan));
        setLoading(false);
      } catch (e) {
        setLoading(true);
      }
    }

    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (!sessionStorage.getItem("dashboard_stats_cache")) {
        setLoading(true);
      }

      const [statsRes, revRes, planRes] = await Promise.all([
        authAxios.get('/api/super-admin/dashboard-stats/'),
        authAxios.get('/api/super-admin/reports/?type=revenue'),
        authAxios.get('/api/super-admin/reports/?type=plans')
      ]);

      if (statsRes.data) {
        setStats(statsRes.data);
        sessionStorage.setItem("dashboard_stats_cache", JSON.stringify(statsRes.data));
      }
      if (revRes.data?.data) {
        setRevenueData(revRes.data.data);
        sessionStorage.setItem("dashboard_rev_cache", JSON.stringify(revRes.data.data));
      }
      if (planRes.data?.data) {
        setPlanDistribution(planRes.data.data);
        sessionStorage.setItem("dashboard_plan_cache", JSON.stringify(planRes.data.data));
      }

    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Executive Dashboard</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium italic">Essential business health indicators</p>
        </div>
        <div className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest leading-none">System Operational</span>
        </div>
      </div>

      {/* MISSION CRITICAL GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MainStatCard
          title="Monthly Revenue"
          value={formatCurrency(stats.monthly_revenue)}
          trend={stats.monthly_growth}
          trendLabel="vs previous month"
          icon={IndianRupee}
          gradient="linear-gradient(135deg, #6366f1 0%, #4338ca 60%, #312e81 100%)"
          accent="#818cf8"
        />
        <MainStatCard
          title="Active Subscriptions"
          value={stats.active_subscriptions}
          subtext="Total paying customers"
          icon={CreditCard}
          gradient="linear-gradient(135deg, #059669 0%, #047857 60%, #064e3b 100%)"
          accent="#34d399"
        />
        <MainStatCard
          title="Expiring Soon"
          value={stats.expiring_soon}
          subtext="Ending in 7 days"
          icon={Calendar}
          gradient="linear-gradient(135deg, #d97706 0%, #b45309 60%, #78350f 100%)"
          accent="#fbbf24"
        />
        <MainStatCard
          title="Failed Payments"
          value={stats.failed_payments}
          subtext="Recoverable revenue churn"
          icon={ShoppingCart}
          gradient="linear-gradient(135deg, #dc2626 0%, #b91c1c 60%, #7f1d1d 100%)"
          accent="#f87171"
        />
      </div>

      {/* Momentum & Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Revenue Momentum Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl backdrop-blur-xl"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/10 rounded-2xl text-purple-500">
                <TrendingUp size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">Revenue Trend</h3>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Platform Growth Momentum</p>
              </div>
            </div>
          </div>

          <div className="h-[300px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(203, 213, 225, 0.15)" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', padding: '12px' }}
                  itemStyle={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                  labelStyle={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#6366f1"
                  strokeWidth={4}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Plan Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl backdrop-blur-xl flex flex-col"
        >
          {/* Card header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-indigo-500/10 rounded-2xl text-indigo-500">
              <PieChart size={20} />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white leading-tight">Plan Distribution</h3>
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Monthly &amp; Yearly Subscriptions</p>
            </div>
          </div>

          {planDistribution.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-8 text-slate-400">
              <PieChart size={40} className="mb-3 opacity-30" />
              <p className="text-sm font-semibold">No subscription data</p>
            </div>
          ) : (() => {
            // Assign colors and ensure every plan gets a visible slice
            // Plans with 0 subscribers get a minimum arc (0.3) so they appear in the donut
            const enriched = planDistribution.map((item, idx) => ({
              ...item,
              color: PLAN_COLORS[idx % PLAN_COLORS.length],
              // Use minimum 0.3 so 0-subscriber plans still show a thin arc
              chartValue: item.subscribers > 0 ? item.subscribers : 0.3,
            }));
            const total = enriched.reduce((s, d) => s + (d.subscribers || 0), 0);

            return (
              <>
                {/* Donut chart — uses chartValue so every plan is visible */}
                <div className="h-[220px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={enriched}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={82}
                        paddingAngle={2}
                        dataKey="chartValue"
                        stroke="none"
                      >
                        {enriched.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            opacity={entry.subscribers > 0 ? 0.92 : 0.28}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '10px 14px',
                        }}
                        itemStyle={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                        labelStyle={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}
                        formatter={(value, name, props) => [
                          `${props.payload.subscribers} subscriber${props.payload.subscribers !== 1 ? 's' : ''}`,
                          props.payload.name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend — shows ALL plans with real subscriber counts */}
                <div className="mt-3 space-y-2 overflow-y-auto max-h-[200px] pr-1">
                  {enriched.map((item, idx) => {
                    const pct = total > 0 ? Math.round((item.subscribers / total) * 100) : 0;
                    return (
                      <div key={idx} className="flex items-center justify-between gap-2">
                        {/* Color swatch + name */}
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-3 h-3 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: item.color, opacity: item.subscribers > 0 ? 1 : 0.4 }}
                          />
                          <span
                            className={`text-[11px] font-semibold truncate ${
                              item.subscribers > 0
                                ? 'text-slate-700 dark:text-slate-200'
                                : 'text-slate-400 dark:text-slate-500'
                            }`}
                            title={item.name}
                          >
                            {item.name}
                          </span>
                        </div>
                        {/* Count + percentage */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span
                            className="text-[10px] font-black px-1.5 py-0.5 rounded-md"
                            style={{
                              backgroundColor: `${item.color}22`,
                              color: item.subscribers > 0 ? item.color : '#94a3b8',
                            }}
                          >
                            {pct}%
                          </span>
                          <span className={`text-xs font-black w-6 text-right ${
                            item.subscribers > 0
                              ? 'text-slate-900 dark:text-white'
                              : 'text-slate-400'
                          }`}>
                            {item.subscribers}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total row */}
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Total Active</span>
                  <span className="text-sm font-black text-slate-900 dark:text-white">{total}</span>
                </div>
              </>
            );
          })()}
        </motion.div>
      </div>
    </div>
  );
}
